/* /forgot-password — demande d'un lien de réinitialisation par email.
 * Backend : POST /auth/password/forgot (always 200, no enumeration).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Mail, AlertTriangle, CheckCircle2, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  useSEO({
    title: "Mot de passe oublié — Noam",
    description: "Demande de réinitialisation du mot de passe Noam. Un lien sécurisé sera envoyé par email.",
    path: "/forgot-password",
  });
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/auth/password/forgot`, { email: email.trim() }, { withCredentials: true });
      setSent(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d && d.msg) ? d.msg : (err.message || "Erreur."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--noam-bg)] text-white" data-testid="forgot-root">
      <div className="absolute inset-0 noam-grid-bg opacity-30 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] noam-glow pointer-events-none" />

      <header className="relative px-6 pt-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#378ADD]/20 border border-[#378ADD]/40 flex items-center justify-center">
            <span className="noam-display font-bold text-[#378ADD]">N</span>
          </div>
          <div className="leading-tight">
            <div className="noam-display text-base font-semibold">Noam</div>
            <div className="text-[10px] text-zinc-500 -mt-0.5">by Constante Partners</div>
          </div>
        </Link>
        <LanguageSwitcher compact />
      </header>

      <main className="relative flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="noam-card p-8">
            <div className="text-[#378ADD] text-[11px] uppercase tracking-[0.18em] font-semibold">
              {t("auth.forgot_overline", { defaultValue: "Réinitialisation" })}
            </div>
            <h1 className="noam-display text-2xl font-bold mt-2">
              {t("auth.forgot_title", { defaultValue: "Mot de passe oublié ?" })}
            </h1>
            <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
              {t("auth.forgot_subtitle", { defaultValue: "Saisissez votre email. Si un compte existe, vous recevrez un lien valable 24h." })}
            </p>

            {sent ? (
              <div className="mt-7 space-y-4" data-testid="forgot-sent">
                <div className="flex items-start gap-2 p-4 rounded-md border border-[#1D9E75]/30 bg-[#1D9E75]/10 text-[#86EFAC] text-sm">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{t("auth.forgot_sent", { defaultValue: "Si un compte correspond à cet email, un lien de réinitialisation vient d'être généré (valable 24h)." })}</span>
                </div>
                <p className="text-zinc-500 text-xs">
                  {t("auth.forgot_no_email", { defaultValue: "Vous n'avez pas reçu d'email ? Contactez l'Ultra Admin de la plateforme — il peut générer un lien de récupération directement." })}
                </p>
                <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-[#378ADD] hover:underline" data-testid="forgot-back-to-login">
                  <ArrowLeft className="w-3 h-3" />
                  {t("auth.back_to_login", { defaultValue: "Retour à la connexion" })}
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-7 space-y-4" data-testid="forgot-form">
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider font-medium mb-1.5 block">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="noam-input"
                    placeholder="vous@organisation.com"
                    data-testid="forgot-email-input"
                    autoComplete="email"
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="btn-primary w-full justify-center disabled:opacity-50"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> {t("auth.forgot_sending", { defaultValue: "Envoi…" })}</>
                  ) : (
                    <><Mail className="w-4 h-4" /> {t("auth.forgot_submit", { defaultValue: "Envoyer le lien" })}</>
                  )}
                </button>

                <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200" data-testid="forgot-back-to-login-2">
                  <ArrowLeft className="w-3 h-3" />
                  {t("auth.back_to_login", { defaultValue: "Retour à la connexion" })}
                </Link>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
